import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguageAwareRender } from '../../hooks/useLanguageAwareRender';
import { t } from '../../utils/localization/localization';
import { ErrorState, tableShellSx, responsiveTableContainerSx } from '../common';

export default function RoleManagement() {
  const { ROLES } = useAuth();

  // Use custom hook for language-aware rendering
  const languageRenderKey = useLanguageAwareRender('RoleManagement');

  const [roles, setRoles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const roleTableColumnSx = {
    role: { width: { xs: '24%', md: '22%' }, minWidth: { xs: 160, md: 200 } },
    description: { width: { xs: '49%', md: '52%' }, minWidth: { xs: 260, md: 360 } },
    permissions: { width: { xs: '17%', md: '18%' }, minWidth: { xs: 120, md: 150 }, whiteSpace: 'nowrap' },
    actions: { width: { xs: '1%', md: '8%' }, minWidth: 96, whiteSpace: 'nowrap' }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch(role) {
      case ROLES.ADMIN:
        return 'error';
      case ROLES.MANAGER:
        return 'warning';
      case ROLES.USER:
        return 'success';
      default:
        return 'default';
    }
  };

  // All available permissions
  const allPermissions = useMemo(() => [
    { id: 'view_own_analyses', name: 'View Own Analyses', category: 'Analysis' },
    { id: 'create_cv_analysis', name: 'Create CV Analysis', category: 'Analysis' },
    { id: 'create_job_analysis', name: 'Create Job Analysis', category: 'Analysis' },
    { id: 'view_all_analyses', name: 'View All Analyses', category: 'Analysis' },
    { id: 'create_company_research', name: 'Create Company Research', category: 'Research' },
    { id: 'view_public_reports', name: 'View Public Reports', category: 'Reports' },
    { id: 'view_analytics', name: 'View Analytics', category: 'Analytics' },
    { id: 'manage_analysts', name: 'Manage Analysts', category: 'User Management' },
    { id: 'manage_users', name: 'Manage Users', category: 'User Management' },
    { id: 'manage_roles', name: 'Manage Roles', category: 'User Management' },
    { id: 'system_configuration', name: 'System Configuration', category: 'Administration' }
  ], []);

  // Group permissions by category
  const permissionsByCategory = allPermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});

  // Load demo roles
  useEffect(() => {
    // In a real app, this would fetch roles from your backend
    const demoRoles = [
      {
        id: ROLES.ADMIN,
        name: 'Administrator',
        description: 'Full system access with all permissions',
        permissions: allPermissions.map(p => p.id)
      },
      {
        id: ROLES.MANAGER,
        name: 'Manager',
        description: 'Can manage users and view all analyses',
        permissions: ['view_own_analyses', 'create_cv_analysis', 'create_job_analysis',
          'view_all_analyses', 'create_company_research', 'view_analytics', 'manage_analysts']
      },
      {
        id: ROLES.USER,
        name: 'User',
        description: 'Basic access to create CV analyses and view own results',
        permissions: ['view_own_analyses', 'create_cv_analysis', 'view_public_reports']
      }
    ];

    setRoles(demoRoles);
  }, [ROLES, allPermissions]);

  const handleOpenDialog = (role) => {
    setSelectedRole(role);

    setFormData({
      name: role.name,
      description: role.description,
      permissions: [...role.permissions]
    });

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];

      if (permissions.includes(permissionId)) {
        // Remove permission
        return {
          ...prev,
          permissions: permissions.filter(id => id !== permissionId)
        };
      } else {
        // Add permission
        return {
          ...prev,
          permissions: [...permissions, permissionId]
        };
      }
    });
  };

  const handleSubmit = () => {
    try {
      // Validate form
      if (!formData.name || !formData.description) {
        setError('Please fill in all required fields');
        return;
      }

      // Update role
      setRoles(prev =>
        prev.map(role =>
          role.id === selectedRole.id
            ? {
              ...role,
              name: formData.name,
              description: formData.description,
              permissions: formData.permissions
            }
            : role
        )
      );

      setSuccess('Role updated successfully');
      handleCloseDialog();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError('Error: ' + error.message);
    }
  };

  return (
    <Box key={languageRenderKey}>
      {success && (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={success}
            message={success}
            severity="success"
            component="RoleManagement-Success"
            showRetry={false}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {t('admin.roleManagement')}
        </Typography>
      </Box>

      <Box sx={tableShellSx}>
        <TableContainer component={Paper} sx={responsiveTableContainerSx}>
          <Table sx={{ tableLayout: 'fixed', minWidth: { xs: 760, md: 900 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={roleTableColumnSx.role}>{t('admin.role')}</TableCell>
                <TableCell sx={roleTableColumnSx.description}>{t('admin.description')}</TableCell>
                <TableCell sx={roleTableColumnSx.permissions}>{t('admin.permissions')}</TableCell>
                <TableCell className="table-actions-cell" sx={roleTableColumnSx.actions}>{t('admin.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell sx={roleTableColumnSx.role}>
                    <Chip
                      label={role.name}
                      color={getRoleColor(role.id)}
                    />
                  </TableCell>
                  <TableCell sx={roleTableColumnSx.description}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{role.description}</Typography>
                  </TableCell>
                  <TableCell sx={roleTableColumnSx.permissions}>
                    <Chip size="small" label={t('admin.permissionsCount', { count: role.permissions.length })} />
                  </TableCell>
                  <TableCell className="table-actions-cell" sx={roleTableColumnSx.actions}>
                    <Tooltip title={t('admin.editRole')}>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(role)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Edit Role Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('admin.editRole')}: {selectedRole?.name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Box sx={{ mb: 2 }}>
              <ErrorState
                error={error}
                message={error}
                severity="error"
                component="RoleManagement-Dialog"
                showRetry={false}
              />
            </Box>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            label="Role Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={selectedRole?.id === ROLES.ADMIN} // Don't allow editing admin role name
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Permissions
          </Typography>

          {Object.entries(permissionsByCategory).map(([category, permissions]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {category}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <FormGroup>
                {permissions.map(permission => (
                  <FormControlLabel
                    key={permission.id}
                    control={
                      <Checkbox
                        checked={formData.permissions.includes(permission.id)}
                        onChange={() => handlePermissionChange(permission.id)}
                        disabled={selectedRole?.id === ROLES.ADMIN} // Don't allow editing admin permissions
                      />
                    }
                    label={permission.name}
                  />
                ))}
              </FormGroup>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={selectedRole?.id === ROLES.ADMIN} // Don't allow saving changes to admin role
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
